
import { useState, useMemo } from "react";

export const useCustomerFilters = (customers: any[], searchTerm: string) => {
  const [filters, setFilters] = useState({
    nameFilter: "",
    platformFilter: "",
    statusFilter: "",
    dddFilter: "",
    minSpendFilter: "",
    maxSpendFilter: "",
    minPurchasesFilter: "",
  });

  const hasActiveFilters = useMemo(() => {
    return Object.values(filters).some(filter => filter !== "");
  }, [filters]);

  const filteredCustomers = useMemo(() => {
    let filtered = customers;

    // Apply search term filter
    if (searchTerm) {
      filtered = filtered.filter(customer =>
        customer.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        customer.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        customer.phone?.includes(searchTerm)
      );
    }

    // Apply specific filters
    if (filters.nameFilter) {
      filtered = filtered.filter(customer =>
        customer.name.toLowerCase().includes(filters.nameFilter.toLowerCase())
      );
    }

    if (filters.platformFilter) {
      filtered = filtered.filter(customer =>
        (customer.platform || 'nuvemshop').toLowerCase() === filters.platformFilter.toLowerCase()
      );
    }

    if (filters.statusFilter) {
      filtered = filtered.filter(customer =>
        (customer.status || 'active').toLowerCase() === filters.statusFilter.toLowerCase()
      );
    }

    if (filters.dddFilter) {
      filtered = filtered.filter(customer => {
        if (!customer.phone) return false;
        const ddd = customer.phone.replace(/\D/g, '').substring(0, 2);
        return ddd === filters.dddFilter;
      });
    }

    if (filters.minSpendFilter) {
      const minSpend = parseFloat(filters.minSpendFilter);
      filtered = filtered.filter(customer => {
        const totalSpent = customer.total_spent || customer.totalSpent || 0;
        return totalSpent >= minSpend;
      });
    }

    if (filters.maxSpendFilter) {
      const maxSpend = parseFloat(filters.maxSpendFilter);
      filtered = filtered.filter(customer => {
        const totalSpent = customer.total_spent || customer.totalSpent || 0;
        return totalSpent <= maxSpend;
      });
    }

    if (filters.minPurchasesFilter) {
      const minPurchases = parseInt(filters.minPurchasesFilter);
      filtered = filtered.filter(customer => {
        const purchases = customer.orders_count || customer.purchases || 0;
        return purchases >= minPurchases;
      });
    }

    return filtered;
  }, [customers, searchTerm, filters]);

  const applyFilters = (newFilters: typeof filters) => {
    setFilters(newFilters);
  };

  const clearFilters = () => {
    setFilters({
      nameFilter: "",
      platformFilter: "",
      statusFilter: "",
      dddFilter: "",
      minSpendFilter: "",
      maxSpendFilter: "",
      minPurchasesFilter: "",
    });
  };

  return {
    filters,
    applyFilters,
    clearFilters,
    filteredCustomers,
    hasActiveFilters,
  };
};
